(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initReveals(scope) {
    var reveals = scope.querySelectorAll('.c02-reveal:not([data-c02-reveal-ready])');
    if (!reveals.length) return;

    document.documentElement.classList.add('c02-motion-ready');

    Array.prototype.forEach.call(reveals, function (item) {
      item.setAttribute('data-c02-reveal-ready', 'true');
    });

    if (reduceMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(reveals, function (item) {
        item.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.07, rootMargin: '0px 0px -3%' });

    Array.prototype.forEach.call(reveals, function (item) {
      observer.observe(item);
    });
  }

  function initCarousel(root) {
    if (!root || root.dataset.c02CarouselReady === 'true') return;

    var track = root.querySelector('[data-c02-track]');
    var slides = track ? Array.prototype.slice.call(track.querySelectorAll('[data-c02-slide]')) : [];
    var current = root.querySelector('[data-c02-current]');
    var previous = root.querySelector('[data-c02-previous]');
    var next = root.querySelector('[data-c02-next]');

    if (!track || !slides.length) return;

    function closestSlideIndex() {
      var trackLeft = track.getBoundingClientRect().left;
      var closestIndex = 0;
      var closestDistance = Infinity;

      slides.forEach(function (slide, index) {
        var distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    function updateCurrent() {
      var activeIndex = closestSlideIndex();

      if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
      slides.forEach(function (slide, index) {
        slide.classList.toggle('is-current', index === activeIndex);
      });
    }

    function goToSlide(index) {
      var wrappedIndex = (index + slides.length) % slides.length;
      slides[wrappedIndex].scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }

    var scrollFrame = 0;
    track.addEventListener('scroll', function () {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(updateCurrent);
    }, { passive: true });

    track.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      goToSlide(closestSlideIndex() + (event.key === 'ArrowRight' ? 1 : -1));
    });

    if (previous) previous.addEventListener('click', function () {
      goToSlide(closestSlideIndex() - 1);
    });

    if (next) next.addEventListener('click', function () {
      goToSlide(closestSlideIndex() + 1);
    });

    updateCurrent();
    root.dataset.c02CarouselReady = 'true';
  }

  function initTransformation(root) {
    if (!root || root.dataset.c02TransformationReady === 'true') return;

    var stage = root.querySelector('.c02-transformation__stage');
    var scrollFrame = 0;
    var lastProgress = -1;

    if (!stage) return;

    root.dataset.c02TransformationReady = 'true';

    if (reduceMotion) {
      stage.style.setProperty('--c02-transformation-clip', '0%');
      return;
    }

    function update() {
      scrollFrame = 0;

      var bounds = stage.getBoundingClientRect();
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      var visibleBefore = Math.min(bounds.height * 0.92, viewportHeight * 0.6);
      var start = viewportHeight - visibleBefore;
      var end = Math.max(48, viewportHeight * 0.08);
      var progress = Math.max(0, Math.min(1, (start - bounds.top) / (start - end)));

      if (Math.abs(progress - lastProgress) < 0.001) return;

      var clip = (1 - progress) * 100;
      var lineOpacity = Math.min(1, progress / 0.06, (1 - progress) / 0.06);

      stage.style.setProperty('--c02-transformation-clip', clip.toFixed(3) + '%');
      stage.style.setProperty('--c02-transformation-line-opacity', lineOpacity.toFixed(3));
      lastProgress = progress;
    }

    function requestUpdate() {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    update();

    root.c02TransformationCleanup = function () {
      window.cancelAnimationFrame(scrollFrame);
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }

  function initObjectLeaders(root) {
    var study = root && root.querySelector('.c02-object__study');
    if (!study || study.dataset.c02ObjectLeadersReady === 'true') return;

    var paths = study.querySelectorAll('.c02-object__leader-line');
    var canvasLabel = study.querySelector('.c02-object__callout--canvas');
    var frameLabel = study.querySelector('.c02-object__callout--frame');
    var desktopQuery = window.matchMedia && window.matchMedia('(min-width: 990px)');
    var resizeObserver = null;
    var resizeFrame = 0;
    var originalCanvasPath = 'M 41 14 H 54 L 48 47';
    var originalFramePath = 'M 52 112 H 81 L 92.5 58';

    if (paths.length < 2 || !canvasLabel || !frameLabel) return;

    study.dataset.c02ObjectLeadersReady = 'true';

    function coordinate(value, origin, extent, viewBoxExtent) {
      return ((value - origin) / extent * viewBoxExtent).toFixed(2);
    }

    function update() {
      resizeFrame = 0;

      if (!desktopQuery || !desktopQuery.matches) {
        paths[0].setAttribute('d', originalCanvasPath);
        paths[1].setAttribute('d', originalFramePath);
        return;
      }

      var studyBounds = study.getBoundingClientRect();
      var canvasBounds = canvasLabel.getBoundingClientRect();
      var frameBounds = frameLabel.getBoundingClientRect();

      if (!studyBounds.width || !studyBounds.height) return;

      var canvasX = coordinate(canvasBounds.right, studyBounds.left, studyBounds.width, 100);
      var canvasY = coordinate(canvasBounds.top + canvasBounds.height / 2, studyBounds.top, studyBounds.height, 125);
      var frameX = coordinate(frameBounds.left, studyBounds.left, studyBounds.width, 100);
      var frameY = coordinate(frameBounds.top + frameBounds.height / 2, studyBounds.top, studyBounds.height, 125);

      paths[0].setAttribute('d', 'M ' + canvasX + ' ' + canvasY + ' H 54 L 48 47');
      paths[1].setAttribute('d', 'M ' + frameX + ' ' + frameY + ' H 81 L 92.5 58');
    }

    function requestUpdate() {
      if (resizeFrame) return;
      resizeFrame = window.requestAnimationFrame(update);
    }

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(requestUpdate);
      resizeObserver.observe(study);
      resizeObserver.observe(canvasLabel);
      resizeObserver.observe(frameLabel);
    }

    window.addEventListener('resize', requestUpdate, { passive: true });
    window.requestAnimationFrame(update);

    study.c02ObjectLeadersCleanup = function () {
      window.cancelAnimationFrame(resizeFrame);
      window.removeEventListener('resize', requestUpdate);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }

  function initHeroTransition(hero) {
    if (!hero || hero.dataset.c02HeroTransitionReady === 'true' || reduceMotion) return;

    var link = hero.querySelector('.c02-scroll-link');
    var image = hero.querySelector('.c02-hero__media img');
    var target = link && document.querySelector(link.getAttribute('href'));
    var scrollFrame = 0;
    var entering = false;
    var touchStartY = null;

    if (!link || !image || !target) return;

    if (typeof image.decode === 'function') {
      image.decode().catch(function () {});
    }

    function pageHasMoved() {
      var bounds = hero.getBoundingClientRect();
      return bounds.top < -6 && bounds.bottom > 0;
    }

    function updateFromScroll() {
      scrollFrame = 0;
      if (entering) return;
      hero.classList.toggle('is-room-preview', pageHasMoved());
    }

    function onScroll() {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(updateFromScroll);
    }

    function onTouchStart(event) {
      if (entering || !event.touches || event.touches.length !== 1) return;
      touchStartY = event.touches[0].clientY;
    }

    function onTouchMove(event) {
      if (entering || touchStartY === null || !event.touches || event.touches.length !== 1) return;
      if (touchStartY - event.touches[0].clientY > 6) showPreview();
    }

    function onTouchEnd() {
      touchStartY = null;
      onScroll();
    }

    function showPreview() {
      hero.classList.add('is-room-preview');
    }

    function releasePreview() {
      if (!pageHasMoved() && !entering) hero.classList.remove('is-room-preview');
    }

    function enterRoom(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!image.complete || !image.naturalWidth) return;

      event.preventDefault();
      entering = true;
      hero.classList.remove('is-room-preview');
      hero.classList.add('is-room-entering');

      window.setTimeout(function () {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 620);
    }

    hero.classList.add('is-room-transition-ready');
    link.addEventListener('mouseenter', showPreview);
    link.addEventListener('mouseleave', releasePreview);
    link.addEventListener('focus', showPreview);
    link.addEventListener('blur', releasePreview);
    link.addEventListener('pointerdown', showPreview, { passive: true });
    link.addEventListener('click', enterRoom);
    window.addEventListener('scroll', onScroll, { passive: true });
    hero.addEventListener('touchstart', onTouchStart, { passive: true });
    hero.addEventListener('touchmove', onTouchMove, { passive: true });
    hero.addEventListener('touchend', onTouchEnd, { passive: true });
    hero.addEventListener('touchcancel', onTouchEnd, { passive: true });
    updateFromScroll();

    hero.c02TransitionCleanup = function () {
      window.cancelAnimationFrame(scrollFrame);
      window.removeEventListener('scroll', onScroll);
      link.removeEventListener('mouseenter', showPreview);
      link.removeEventListener('mouseleave', releasePreview);
      link.removeEventListener('focus', showPreview);
      link.removeEventListener('blur', releasePreview);
      link.removeEventListener('pointerdown', showPreview);
      link.removeEventListener('click', enterRoom);
      hero.removeEventListener('touchstart', onTouchStart);
      hero.removeEventListener('touchmove', onTouchMove);
      hero.removeEventListener('touchend', onTouchEnd);
      hero.removeEventListener('touchcancel', onTouchEnd);
    };

    hero.dataset.c02HeroTransitionReady = 'true';
  }

  function compileShader(gl, type, source) {
    var shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      return null;
    }

    return shader;
  }

  function initSignal(root) {
    var canvas = root && root.querySelector('[data-c02-signal]');
    if (!canvas || canvas.dataset.c02SignalReady === 'true') return;

    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    var lowMemory = typeof navigator.deviceMemory === 'number' && navigator.deviceMemory < 4;
    var saveData = connection && connection.saveData;
    var userAgent = navigator.userAgent || '';
    var isSafari = /Safari/i.test(userAgent)
      && /Apple Computer/i.test(navigator.vendor || '')
      && !/(CriOS|FxiOS|EdgiOS|OPiOS)/i.test(userAgent);

    canvas.dataset.c02SignalReady = 'true';

    function useStaticSignal() {
      canvas.classList.add('is-static');
      root.classList.add('is-signal-static');
    }

    if (reduceMotion || saveData || lowMemory) {
      useStaticSignal();
      return;
    }

    if (isSafari) canvas.classList.add('is-safari-webgl');

    var gl = null;

    try {
      gl = canvas.getContext('webgl', {
        alpha: true,
        antialias: false,
        depth: false,
        stencil: false,
        premultipliedAlpha: isSafari,
        preserveDrawingBuffer: false,
        powerPreference: 'low-power'
      });
    } catch (error) {
      gl = null;
    }

    if (!gl) {
      try {
        gl = canvas.getContext('experimental-webgl');
      } catch (error) {
        gl = null;
      }
    }

    if (!gl) {
      useStaticSignal();
      return;
    }

    var vertexSource = [
      'attribute vec2 a_position;',
      'void main() {',
      '  gl_Position = vec4(a_position, 0.0, 1.0);',
      '}'
    ].join('\n');

    var fragmentSource = [
      'precision mediump float;',
      'uniform vec2 u_resolution;',
      'uniform vec2 u_pointer;',
      'uniform float u_time;',
      'void main() {',
      '  vec2 uv = gl_FragCoord.xy / u_resolution.xy;',
      '  vec2 p = uv - 0.5;',
      '  p.x *= u_resolution.x / u_resolution.y;',
      '  vec2 pointer = u_pointer - 0.5;',
      '  pointer.x *= u_resolution.x / u_resolution.y;',
      '  float distanceToPointer = length(p - pointer);',
      '  float pulse = exp(-distanceToPointer * 2.6) * 0.24;',
      '  float field = p.x * 3.4 + p.y * 1.2;',
      '  field += sin(p.y * 5.2 - u_time * 0.13) * 0.42;',
      '  field += sin((p.x + p.y) * 3.1 + u_time * 0.09) * 0.31;',
      '  field += sin(distanceToPointer * 8.0 - u_time * 0.18) * pulse;',
      '  float contour = abs(sin(field * 5.4));',
      '  float line = 1.0 - smoothstep(0.0, 0.075, contour);',
      '  float halo = 1.0 - smoothstep(0.0, 0.28, contour);',
      '  float edgeFade = smoothstep(0.02, 0.32, uv.x) * smoothstep(0.02, 0.24, 1.0 - uv.x);',
      '  vec3 bronze = vec3(0.57, 0.45, 0.34);',
      '  vec3 mineral = vec3(0.96, 0.95, 0.91);',
      '  vec3 color = mix(bronze, mineral, clamp(uv.y * 0.75 + pulse, 0.0, 1.0));',
      '  float alpha = (line * 0.28 + halo * 0.055) * edgeFade;',
      isSafari
        ? '  gl_FragColor = vec4(color * alpha, alpha);'
        : '  gl_FragColor = vec4(color, alpha);',
      '}'
    ].join('\n');

    var vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
    var fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);

    if (!vertexShader || !fragmentShader) {
      useStaticSignal();
      return;
    }

    var program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      useStaticSignal();
      return;
    }

    gl.useProgram(program);

    var buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1, 1, -1, -1, 1,
      -1, 1, 1, -1, 1, 1
    ]), gl.STATIC_DRAW);

    var position = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    var resolution = gl.getUniformLocation(program, 'u_resolution');
    var pointerUniform = gl.getUniformLocation(program, 'u_pointer');
    var timeUniform = gl.getUniformLocation(program, 'u_time');
    var mobile = window.matchMedia('(max-width: 749px), (pointer: coarse)').matches;
    var pointer = [0.74, 0.56];
    var targetPointer = [0.74, 0.56];
    var frame = 0;
    var resizeFrame = 0;
    var active = false;
    var inView = true;
    var contextLost = false;
    var lastPaint = 0;
    var startTime = performance.now();
    var frameInterval = mobile ? 1000 / 30 : 0;

    function resize() {
      var rect = canvas.getBoundingClientRect();
      var dpr = mobile ? 1 : Math.min(window.devicePixelRatio || 1, 1.25);
      var renderScale = mobile ? 0.55 : 0.72;
      var width = Math.max(2, Math.round(rect.width * dpr * renderScale));
      var height = Math.max(2, Math.round(rect.height * dpr * renderScale));

      if (canvas.width === width && canvas.height === height) return;
      canvas.width = width;
      canvas.height = height;
      gl.viewport(0, 0, width, height);
      gl.uniform2f(resolution, width, height);
    }

    function render(timestamp) {
      if (!active || contextLost) return;
      frame = window.requestAnimationFrame(render);

      if (frameInterval && timestamp - lastPaint < frameInterval) return;
      lastPaint = timestamp;

      pointer[0] += (targetPointer[0] - pointer[0]) * 0.035;
      pointer[1] += (targetPointer[1] - pointer[1]) * 0.035;

      gl.uniform1f(timeUniform, (timestamp - startTime) / 1000);
      gl.uniform2f(pointerUniform, pointer[0], pointer[1]);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    function setActive(nextActive) {
      var shouldRun = nextActive && !document.hidden && !contextLost;
      if (shouldRun === active) return;
      active = shouldRun;

      if (active) {
        lastPaint = 0;
        frame = window.requestAnimationFrame(render);
      } else {
        window.cancelAnimationFrame(frame);
      }
    }

    function onResize() {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(resize);
    }

    function onVisibilityChange() {
      setActive(inView);
    }

    function onPointerMove(event) {
      var rect = root.getBoundingClientRect();
      targetPointer[0] = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      targetPointer[1] = Math.max(0, Math.min(1, 1 - ((event.clientY - rect.top) / rect.height)));
    }

    var signalObserver = null;
    if ('IntersectionObserver' in window) {
      signalObserver = new IntersectionObserver(function (entries) {
        inView = Boolean(entries[0] && entries[0].isIntersecting);
        setActive(inView);
      }, { threshold: 0.01 });
      signalObserver.observe(root);
    }

    window.addEventListener('resize', onResize, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);

    if (!mobile && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      root.addEventListener('pointermove', onPointerMove, { passive: true });
    }

    canvas.addEventListener('webglcontextlost', function (event) {
      event.preventDefault();
      contextLost = true;
      setActive(false);
      useStaticSignal();
    });

    resize();
    gl.clearColor(0, 0, 0, 0);
    setActive(true);

    canvas.c02Cleanup = function () {
      setActive(false);
      if (signalObserver) signalObserver.disconnect();
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      root.removeEventListener('pointermove', onPointerMove);
    };
  }

  function init(scope) {
    initReveals(scope);
    var editorials = scope.querySelectorAll('[data-c02-editorial]');

    if (editorials.length) document.documentElement.classList.add('c02-editorial-page');

    Array.prototype.forEach.call(editorials, function (root) {
      initCarousel(root);
      Array.prototype.forEach.call(root.querySelectorAll('[data-c02-transformation]'), initTransformation);
      initObjectLeaders(root);
      var hero = root.querySelector('[data-c02-hero]');
      initHeroTransition(hero);
      initSignal(hero);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var canvas = event.target.querySelector('[data-c02-signal]');
    var hero = event.target.querySelector('[data-c02-hero]');
    var transformation = event.target.querySelector('[data-c02-transformation]');
    var objectStudy = event.target.querySelector('.c02-object__study');
    var editorial = event.target.querySelector('[data-c02-editorial]');
    if (canvas && typeof canvas.c02Cleanup === 'function') canvas.c02Cleanup();
    if (hero && typeof hero.c02TransitionCleanup === 'function') hero.c02TransitionCleanup();
    if (transformation && typeof transformation.c02TransformationCleanup === 'function') transformation.c02TransformationCleanup();
    if (objectStudy && typeof objectStudy.c02ObjectLeadersCleanup === 'function') objectStudy.c02ObjectLeadersCleanup();
    if (editorial) {
      window.requestAnimationFrame(function () {
        if (!document.querySelector('[data-c02-editorial]')) document.documentElement.classList.remove('c02-editorial-page');
      });
    }
  });
})();
